/**
 * Small coercion / object helpers for API payloads and plain records (not vitals-specific).
 */

/**
 * @param {unknown} val
 * @returns {string|null}
 */
export function toStr(val) {
  if (val == null) return null;
  return String(val).trim() || null;
}

/**
 * @param {unknown} val
 * @returns {string[]}
 */
export function toStringArray(val) {
  if (!Array.isArray(val)) return [];
  return val.map((x) => String(x ?? "").trim()).filter(Boolean);
}

/**
 * Copy own properties from `overlay` onto a shallow clone of `base`, skipping null/empty strings.
 * @param {Record<string, unknown>} base
 * @param {Record<string, unknown>} overlay
 * @returns {Record<string, unknown>}
 */
export function mergeNonEmptyValues(base, overlay) {
  const out = { ...base };
  for (const [k, v] of Object.entries(overlay)) {
    if (v == null || String(v).trim() === "") continue;
    out[k] = v;
  }
  return out;
}

/**
 * Normalize API key-value blobs: object entries or array of `{ name, value }`.
 * @param {unknown} val
 * @returns {Record<string, string>}
 */
export function toKeyValue(val) {
  if (!val || typeof val !== "object") return {};
  const entries = Array.isArray(val)
    ? val.map((item) =>
        item && typeof item === "object" && "name" in item
          ? [item.name, item.value ?? ""]
          : []
      )
    : Object.entries(val);
  const out = {};
  for (const e of entries) {
    if (!Array.isArray(e) || e.length < 2) continue;
    const [k, v] = e;
    const key = String(k ?? "").trim();
    if (key) out[key] = v ?? "";
  }
  return out;
}

function getNonEmptyOwn(obj, key) {
  if (!obj || typeof obj !== "object") return undefined;
  const val = obj[key];
  return val != null && val !== "" ? val : undefined;
}

/**
 * First own property among `keys` whose value is non-null and not `""`.
 * @param {Record<string, unknown>} obj
 * @param {string[]} keys
 * @returns {unknown}
 */
export function firstDefinedFromObj(obj, keys) {
  if (!keys || !obj || typeof obj !== "object") return undefined;
  for (const k of keys) {
    const v = getNonEmptyOwn(obj, k);
    if (v !== undefined) return v;
  }
  return undefined;
}

/**
 * @param {unknown} val
 * @param {number} [decimals]
 * @returns {string}
 */
export function formatNumericString(val, decimals = 2) {
  if (val == null) return "";
  if (typeof val === "number") {
    return Number.isInteger(val) ? String(val) : Number(val).toFixed(decimals);
  }
  return String(val).trim();
}
